import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface FindReplaceHighlightOptions {
    matches: Array<{ from: number; to: number }>;
    currentIndex: number;
}

// Global state to store matches
let globalMatches: Array<{ from: number; to: number }> = [];
let globalCurrentIndex = -1;

export const setFindReplaceMatches = (matches: Array<{ from: number; to: number }>, currentIndex: number) => {
    globalMatches = matches;
    globalCurrentIndex = currentIndex;
};

export const clearFindReplaceMatches = () => {
    globalMatches = [];
    globalCurrentIndex = -1;
};

const FindReplaceHighlightPluginKey = new PluginKey('findReplaceHighlight');

export const FindReplaceHighlight = Extension.create<FindReplaceHighlightOptions>({
    name: 'findReplaceHighlight',

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: FindReplaceHighlightPluginKey,
                state: {
                    init() {
                        return DecorationSet.empty;
                    },
                    apply(tr, oldState) {
                        // Get current matches from global state
                        const matches = globalMatches;
                        const currentIndex = globalCurrentIndex;

                        // Check if this is a forced update (when matches change but doc doesn't)
                        const isForcedUpdate = tr.getMeta('findReplaceUpdate');

                        if (matches.length === 0) {
                            return DecorationSet.empty;
                        }

                        // If document changed, map old decorations
                        // If forced update, create new decorations from current matches
                        let decorations: Decoration[];
                        
                        if (isForcedUpdate || !tr.docChanged) {
                            // Create decorations from current matches (positions are already correct)
                            decorations = matches
                                .map((match, index) => {
                                    const isCurrent = index === currentIndex;
                                    
                                    // Validate positions
                                    if (match.from < 0 || match.to > tr.doc.content.size || match.from >= match.to) {
                                        return null;
                                    }
                                    
                                    return Decoration.inline(match.from, match.to, {
                                        class: isCurrent 
                                            ? 'find-replace-highlight find-replace-highlight-current' 
                                            : 'find-replace-highlight',
                                    });
                                })
                                .filter((dec): dec is Decoration => dec !== null);
                        } else {
                            // Document changed - map positions through transaction
                            decorations = matches
                                .map((match, index) => {
                                    const isCurrent = index === currentIndex;
                                    // Map positions through transaction
                                    const from = tr.mapping.map(match.from, -1);
                                    const to = tr.mapping.map(match.to, 1);
                                    
                                    // Validate positions
                                    if (from < 0 || to > tr.doc.content.size || from >= to) {
                                        return null;
                                    }
                                    
                                    return Decoration.inline(from, to, {
                                        class: isCurrent 
                                            ? 'find-replace-highlight find-replace-highlight-current' 
                                            : 'find-replace-highlight',
                                    });
                                })
                                .filter((dec): dec is Decoration => dec !== null);
                        }

                        return DecorationSet.create(tr.doc, decorations);
                    },
                },
                props: {
                    decorations(state) {
                        const pluginState = FindReplaceHighlightPluginKey.getState(state);
                        return pluginState || DecorationSet.empty;
                    },
                },
            }),
        ];
    },
});

